import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Image as ImageIcon, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingState } from "../../components/Ui";
import { getAdminWedding, updateAdminWedding } from "../../lib/api";
import type {
  WeddingGalleryImage,
  WeddingInvitation,
  WeddingStoryItem,
} from "../../types/content";
import { AdminHeader, Field, Notice } from "./AdminUi";

function dateTimeInputValue(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function WeddingEditor() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const template = searchParams.get("template") === "v2" ? "v2" : "v1";
  const query = useQuery({
    queryKey: ["admin-wedding", template],
    queryFn: () => getAdminWedding(template),
  });
  const [invitation, setInvitation] = useState<WeddingInvitation | null>(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setInvitation(query.data || null);
    setNotice("");
  }, [query.data, template]);

  const mutation = useMutation({
    mutationFn: () => updateAdminWedding(invitation!),
    onSuccess: (saved) => {
      setInvitation(saved);
      setNotice("Undangan berhasil disimpan dan langsung dipublikasikan.");
      queryClient.invalidateQueries({
        queryKey: ["wedding-invitation", template],
      });
    },
  });

  if (query.isLoading || !invitation) {
    return <LoadingState label="Membuka editor undangan" />;
  }

  function patch(values: Partial<WeddingInvitation>) {
    setInvitation((current) => (current ? { ...current, ...values } : current));
  }

  function patchStory(index: number, values: Partial<WeddingStoryItem>) {
    patch({
      story: invitation!.story.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...values } : item,
      ),
    });
  }

  function patchGallery(index: number, values: Partial<WeddingGalleryImage>) {
    patch({
      gallery: invitation!.gallery.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...values } : item,
      ),
    });
  }

  return (
    <>
      <AdminHeader
        title="Undangan pernikahan"
        description={`Kelola seluruh teks, jadwal, lokasi, warna, dan galeri untuk template /wedding/${template}.`}
        action={
          <div className="admin-actions">
            <a
              className="button button-secondary"
              href={`/wedding/${template}?m=${encodeURIComponent(
                invitation.groomName,
              )}&f=${encodeURIComponent(
                invitation.brideName,
              )}&kepada=Preview%20Admin`}
              target="_blank"
              rel="noreferrer"
            >
              Pratinjau <Eye size={16} />
            </a>
            <button
              className="button button-primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Menyimpan..." : "Simpan perubahan"} <Save size={16} />
            </button>
          </div>
        }
      />

      {notice && (
        <Notice type="success" message={notice} onClose={() => setNotice("")} />
      )}
      {mutation.isError && (
        <Notice
          type="error"
          message="Perubahan belum tersimpan. Periksa field wajib lalu coba kembali."
          onClose={() => mutation.reset()}
        />
      )}

      <div className="wedding-admin-template-switcher" aria-label="Pilih template wedding">
        <div>
          <span>Template aktif</span>
          <strong>{template === "v1" ? "V1 · Garden Vow" : "V2 · Warm Editorial"}</strong>
        </div>
        <nav>
          {(["v1", "v2"] as const).map((item) => (
            <button
              type="button"
              key={item}
              className={template === item ? "is-active" : ""}
              onClick={() => setSearchParams({ template: item })}
            >
              Template {item.toUpperCase()}
            </button>
          ))}
        </nav>
      </div>

      <div className="wedding-admin-note">
        <div>
          <strong>Link personal tamu</strong>
          <code>/wedding/{template}?m=Nama%20Pria&amp;f=Nama%20Wanita&amp;kepada=Nama%20Tamu</code>
        </div>
        <p>
          Gunakan <code>m=</code> untuk nama mempelai pria, <code>f=</code>{" "}
          untuk nama mempelai wanita, dan <code>kepada=</code> untuk nama
          penerima pada sampul undangan.
        </p>
      </div>

      <div className="wedding-admin-grid">
        <section className="admin-card wedding-admin-card">
          <div className="card-heading">
            <div>
              <span className="wedding-admin-index">01</span>
              <h2>Identitas pasangan</h2>
              <p>Nama yang tampil pada sampul dan profil mempelai.</p>
            </div>
          </div>
          <div className="form-grid">
            <Field label="Nama panggilan mempelai wanita">
              <input
                required
                value={invitation.brideName}
                onChange={(event) => patch({ brideName: event.target.value })}
              />
            </Field>
            <Field label="Nama lengkap mempelai wanita">
              <input
                value={invitation.brideFullName}
                onChange={(event) => patch({ brideFullName: event.target.value })}
              />
            </Field>
            <Field label="Keterangan orang tua mempelai wanita">
              <textarea
                rows={3}
                value={invitation.brideParents}
                onChange={(event) => patch({ brideParents: event.target.value })}
              />
            </Field>
            <Field label="Nama panggilan mempelai pria">
              <input
                required
                value={invitation.groomName}
                onChange={(event) => patch({ groomName: event.target.value })}
              />
            </Field>
            <Field label="Nama lengkap mempelai pria">
              <input
                value={invitation.groomFullName}
                onChange={(event) => patch({ groomFullName: event.target.value })}
              />
            </Field>
            <Field label="Keterangan orang tua mempelai pria">
              <textarea
                rows={3}
                value={invitation.groomParents}
                onChange={(event) => patch({ groomParents: event.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="admin-card wedding-admin-card">
          <div className="card-heading">
            <div>
              <span className="wedding-admin-index">02</span>
              <h2>Pembuka undangan</h2>
              <p>Pesan utama dan kutipan yang menyambut tamu.</p>
            </div>
          </div>
          <div className="form-grid">
            <Field label="Teks pembuka">
              <textarea
                rows={5}
                value={invitation.openingText}
                onChange={(event) => patch({ openingText: event.target.value })}
              />
            </Field>
            <Field label="Kutipan">
              <textarea
                rows={5}
                value={invitation.openingQuote}
                onChange={(event) => patch({ openingQuote: event.target.value })}
              />
            </Field>
            <Field label="URL foto sampul" hint="Gunakan path aset publik atau URL HTTPS.">
              <input
                required
                value={invitation.heroImageUrl}
                onChange={(event) => patch({ heroImageUrl: event.target.value })}
              />
            </Field>
            <div className="wedding-admin-preview">
              <img src={invitation.heroImageUrl} alt="Preview foto sampul undangan" />
              <span><ImageIcon size={14} /> Preview sampul</span>
            </div>
          </div>
        </section>

        <section className="admin-card wedding-admin-card">
          <div className="card-heading">
            <div>
              <span className="wedding-admin-index">03</span>
              <h2>Waktu dan lokasi</h2>
              <p>Informasi acara, countdown, peta, dan kontak konfirmasi.</p>
            </div>
          </div>
          <div className="form-grid">
            <Field label="Tanggal & waktu countdown">
              <input
                type="datetime-local"
                required
                value={dateTimeInputValue(invitation.eventDate)}
                onChange={(event) => {
                  const value = event.target.value;
                  patch({ eventDate: value ? new Date(value).toISOString() : "" });
                }}
              />
            </Field>
            <Field label="Label tanggal">
              <input
                value={invitation.eventDateLabel}
                onChange={(event) => patch({ eventDateLabel: event.target.value })}
              />
            </Field>
            <Field label="Waktu akad">
              <input
                value={invitation.ceremonyTime}
                onChange={(event) => patch({ ceremonyTime: event.target.value })}
              />
            </Field>
            <Field label="Waktu resepsi">
              <input
                value={invitation.receptionTime}
                onChange={(event) => patch({ receptionTime: event.target.value })}
              />
            </Field>
            <Field label="Nama tempat">
              <input
                value={invitation.venueName}
                onChange={(event) => patch({ venueName: event.target.value })}
              />
            </Field>
            <Field label="Alamat lengkap">
              <textarea
                rows={3}
                value={invitation.venueAddress}
                onChange={(event) => patch({ venueAddress: event.target.value })}
              />
            </Field>
            <Field label="URL Google Maps">
              <input
                type="url"
                value={invitation.mapsUrl}
                onChange={(event) => patch({ mapsUrl: event.target.value })}
              />
            </Field>
            <Field label="Nomor WhatsApp RSVP" hint="Gunakan kode negara, contoh 62812...">
              <input
                value={invitation.contactWhatsApp}
                onChange={(event) => patch({ contactWhatsApp: event.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="admin-card wedding-admin-card">
          <div className="card-heading wedding-admin-heading-action">
            <div>
              <span className="wedding-admin-index">04</span>
              <h2>Cerita pasangan</h2>
              <p>Susun momen penting dalam perjalanan pasangan.</p>
            </div>
            <button
              type="button"
              className="button button-secondary"
              onClick={() =>
                patch({
                  story: [
                    ...invitation.story,
                    { period: "Tahun", title: "Momen baru", description: "" },
                  ],
                })
              }
              disabled={invitation.story.length >= 8}
            >
              <Plus size={15} /> Tambah cerita
            </button>
          </div>
          <Field label="Judul bagian cerita">
            <input
              value={invitation.storyTitle}
              onChange={(event) => patch({ storyTitle: event.target.value })}
            />
          </Field>
          <div className="wedding-admin-repeaters">
            {invitation.story.map((item, index) => (
              <article key={index}>
                <div className="wedding-admin-repeater-head">
                  <strong>Bab {String(index + 1).padStart(2, "0")}</strong>
                  <button
                    type="button"
                    aria-label={`Hapus cerita ${index + 1}`}
                    onClick={() =>
                      patch({
                        story: invitation.story.filter((_, itemIndex) => itemIndex !== index),
                      })
                    }
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="form-grid">
                  <Field label="Periode">
                    <input
                      value={item.period}
                      onChange={(event) => patchStory(index, { period: event.target.value })}
                    />
                  </Field>
                  <Field label="Judul">
                    <input
                      value={item.title}
                      onChange={(event) => patchStory(index, { title: event.target.value })}
                    />
                  </Field>
                  <Field label="Cerita">
                    <textarea
                      rows={4}
                      value={item.description}
                      onChange={(event) =>
                        patchStory(index, { description: event.target.value })
                      }
                    />
                  </Field>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="admin-card wedding-admin-card">
          <div className="card-heading wedding-admin-heading-action">
            <div>
              <span className="wedding-admin-index">05</span>
              <h2>Galeri foto</h2>
              <p>Atur hingga 12 foto; susunan akan menyesuaikan otomatis.</p>
            </div>
            <button
              type="button"
              className="button button-secondary"
              onClick={() =>
                patch({
                  gallery: [...invitation.gallery, { imageUrl: "", altText: "" }],
                })
              }
              disabled={invitation.gallery.length >= 12}
            >
              <Plus size={15} /> Tambah foto
            </button>
          </div>
          <Field label="Judul galeri">
            <input
              value={invitation.galleryTitle}
              onChange={(event) => patch({ galleryTitle: event.target.value })}
            />
          </Field>
          <div className="wedding-admin-gallery">
            {invitation.gallery.map((image, index) => (
              <article key={index}>
                <div className="wedding-admin-gallery-image">
                  {image.imageUrl ? (
                    <img src={image.imageUrl} alt={image.altText || `Foto ${index + 1}`} />
                  ) : (
                    <ImageIcon size={26} />
                  )}
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <Field label="URL gambar">
                  <input
                    value={image.imageUrl}
                    onChange={(event) =>
                      patchGallery(index, { imageUrl: event.target.value })
                    }
                  />
                </Field>
                <Field label="Teks alternatif">
                  <input
                    value={image.altText}
                    onChange={(event) =>
                      patchGallery(index, { altText: event.target.value })
                    }
                  />
                </Field>
                <button
                  type="button"
                  className="wedding-admin-remove"
                  aria-label={`Hapus foto ${index + 1}`}
                  onClick={() =>
                    patch({
                      gallery: invitation.gallery.filter(
                        (_, itemIndex) => itemIndex !== index,
                      ),
                    })
                  }
                >
                  <Trash2 size={15} /> Hapus foto
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="admin-card wedding-admin-card">
          <div className="card-heading">
            <div>
              <span className="wedding-admin-index">06</span>
              <h2>Penutup dan warna</h2>
              <p>Pesan terakhir serta palet utama undangan.</p>
            </div>
          </div>
          <div className="form-grid">
            <Field label="Judul penutup">
              <textarea
                rows={3}
                value={invitation.closingTitle}
                onChange={(event) => patch({ closingTitle: event.target.value })}
              />
            </Field>
            <Field label="Pesan penutup">
              <textarea
                rows={3}
                value={invitation.closingMessage}
                onChange={(event) => patch({ closingMessage: event.target.value })}
              />
            </Field>
            <Field label="Warna aksen">
              <div className="wedding-admin-color">
                <input
                  type="color"
                  value={invitation.accentColor}
                  onChange={(event) => patch({ accentColor: event.target.value })}
                />
                <input
                  value={invitation.accentColor}
                  onChange={(event) => patch({ accentColor: event.target.value })}
                />
              </div>
            </Field>
            <Field label="Warna latar">
              <div className="wedding-admin-color">
                <input
                  type="color"
                  value={invitation.backgroundColor}
                  onChange={(event) => patch({ backgroundColor: event.target.value })}
                />
                <input
                  value={invitation.backgroundColor}
                  onChange={(event) => patch({ backgroundColor: event.target.value })}
                />
              </div>
            </Field>
          </div>
        </section>
      </div>

      <div className="wedding-admin-savebar">
        <div>
          <strong>Siap dipublikasikan?</strong>
          <span>Perubahan tampil setelah tombol simpan ditekan.</span>
        </div>
        <button
          className="button button-primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Menyimpan..." : "Simpan semua"} <Save size={16} />
        </button>
      </div>
    </>
  );
}
